import { useQuery, useQueryClient } from 'react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { ROUTES } from '@/utils/constants';
import { apiClient } from '@/api/client';
import LoadingSpinner from '@/components/LoadingSpinner';
import toast from 'react-hot-toast';

const DashboardHome = () => {
  const { userProfile, firebaseUser, setUserProfile } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  // Check for payment success callback
  useEffect(() => {
    const payment = searchParams.get('payment');
    const plan = searchParams.get('plan');
    
    if (payment === 'success' && plan && firebaseUser) {
      // Refresh user profile to get updated plan
      apiClient.getCurrentUser().then((updatedProfile) => {
        // Map backend response to frontend UserProfile format
        const mappedProfile = {
          uid: firebaseUser.uid,
          email: updatedProfile.email || firebaseUser.email || '',
          displayName: updatedProfile.display_name || firebaseUser.displayName || undefined,
          photoURL: firebaseUser.photoURL || undefined,
          plan: (updatedProfile.plan || 'free') as 'free' | 'starter' | 'professional' | 'business' | 'enterprise',
          backendId: updatedProfile.id,
          characters_used_this_month: updatedProfile.characters_used_this_month,
          requests_this_month: updatedProfile.requests_this_month,
          total_characters: updatedProfile.total_characters,
          total_requests: updatedProfile.total_requests,
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setUserProfile(mappedProfile);
        queryClient.invalidateQueries('usage-stats');
        toast.success(`🎉 Successfully upgraded to ${plan.charAt(0).toUpperCase() + plan.slice(1)} plan!`);
        // Remove query params
        setSearchParams({});
      }).catch((error) => {
        console.error('Error refreshing profile:', error);
        toast.error('Payment successful, but failed to refresh profile. Please refresh the page.');
      });
    }
  }, [searchParams, firebaseUser, setUserProfile, queryClient, setSearchParams]);
  
  // Use the new authenticated endpoints
  const { data: keys, isLoading: keysLoading } = useQuery('api-keys', () => apiClient.getUserAPIKeys(), {
    enabled: !!firebaseUser,
  });
  
  const { data: usageStats } = useQuery('usage-stats', () => apiClient.getUserUsageStats(), {
    enabled: !!firebaseUser,
  });

  const stats = [
    {
      label: 'API Keys',
      value: keys?.length || 0,
      icon: '🔑',
      color: 'bg-primary',
      link: ROUTES.API_KEYS,
    },
    {
      label: 'Total Requests',
      value: usageStats?.total_requests?.toLocaleString() || keys?.reduce((sum, key) => sum + (key.total_requests || 0), 0).toLocaleString() || '0',
      icon: '📊',
      color: 'bg-success',
      link: ROUTES.USAGE,
    },
    {
      label: 'Characters This Month',
      value: usageStats?.characters_used_this_month?.toLocaleString() || '0',
      icon: '📝',
      color: 'bg-warning',
      link: ROUTES.USAGE,
    },
    {
      label: 'Current Plan',
      value: (usageStats?.plan || userProfile?.plan || 'free').toUpperCase(),
      icon: '💎',
      color: 'bg-info',
      link: ROUTES.PRICING,
    },
  ];

  if (keysLoading) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div>
      {/* Welcome Section */}
      <div className="mb-40">
        <h1 className="title-one mb-2" style={{ fontSize: '36px', fontWeight: '700' }}>
          Welcome back{userProfile?.displayName ? `, ${userProfile.displayName}` : ''}!
        </h1>
        <p className="text-dark fs-18">
          Manage your API keys, monitor usage, and get the most out of ameboGPT.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="row gx-4 mb-40">
        {stats.map((stat, index) => (
          <div key={index} className="col-lg-3 col-md-6 mb-20">
            <Link
              to={stat.link}
              className="card-style-one border-30 bg-white h-100 text-decoration-none d-block"
              style={{ transition: 'transform 0.2s, box-shadow 0.2s' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '';
              }}
            >
              <div className="d-flex align-items-center">
                <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${stat.color}`} style={{ width: '56px', height: '56px', fontSize: '24px' }}>
                  {stat.icon}
                </div>
                <div className="flex-fill">
                  <div className="text-dark mb-1" style={{ fontSize: '14px', opacity: 0.7 }}>
                    {stat.label}
                  </div>
                  <div className="fw-600 text-dark" style={{ fontSize: '24px' }}>
                    {stat.value}
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="row mb-40">
        <div className="col-lg-6 mb-20">
          <div className="card-style-one border-30 bg-white h-100">
            <h3 className="title mb-3">Quick Start</h3>
            <p className="text-dark mb-4">
              Get started by creating your first API key. It only takes a few seconds.
            </p>
            <Link to={ROUTES.API_KEYS} className="btn-one">
              Create API Key
            </Link>
          </div>
        </div>
        <div className="col-lg-6 mb-20">
          <div className="card-style-one border-30 bg-white h-100">
            <h3 className="title mb-3">Documentation</h3>
            <p className="text-dark mb-4">
              Learn how to integrate ameboGPT into your applications with our comprehensive docs.
            </p>
            <Link to={ROUTES.DOCS} className="btn-two">
              View Docs
            </Link>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {keys && keys.length > 0 && (
        <div className="card-style-one border-30 bg-white">
          <h3 className="title mb-4">Recent API Keys</h3>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Plan</th>
                  <th>Requests</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {keys.slice(0, 5).map((key) => (
                  <tr key={key.id}>
                    <td className="fw-500">{key.name || 'Unnamed Key'}</td>
                    <td>
                      <span className="badge bg-primary">{key.plan}</span>
                    </td>
                    <td>{key.total_requests.toLocaleString()}</td>
                    <td>
                      {key.is_active ? (
                        <span className="badge bg-success">Active</span>
                      ) : (
                        <span className="badge bg-danger">Revoked</span>
                      )}
                    </td>
                    <td>
                      <Link to={ROUTES.API_KEYS} className="text-primary text-decoration-none">
                        Manage →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {keys.length > 5 && (
            <div className="text-center mt-4">
              <Link to={ROUTES.API_KEYS} className="text-primary text-decoration-none">
                View all {keys.length} keys →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardHome;

